import React, { useCallback, useEffect, useMemo } from "react";
import {
  COLLECTION_ID,
  FileEntity,
  Track,
} from "@highnote/server/src/core/entities";
import { getDefaultVersionId } from "@highnote/server/src/core/shared-util";
import { highnote } from "@highnote/server/src/sdk";
import {
  BaseRow,
  BaseTableRowProps,
  calculateChildIndentation,
  EXPAND_COLUMN_WIDTH,
  EXPAND_COLUMN_WIDTH_MOBILE,
} from "App/common/BaseTableV2";
import { Skeleton } from "App/common/Skeleton/Skeleton";
import { useViewport } from "App/common/useViewport";
import { useDnDMonitor } from "App/components/DragAndDrop/DnDMonitor";
import { DnDWrapper } from "App/components/DragAndDrop/DnDWrapper";
import { useGlobalAudioPlayer } from "App/components/GlobalAudioPlayer";
import { useTrackFiles } from "App/components/useEntities/useTrack";
import { useTrackComments } from "App/components/useEntities/useTrackComments/useTrackComments";
import { useEntitiesSelection } from "App/components/useEntitiesSelection";
import { SORT_TYPE } from "App/routes/Main/Library/config";
import { SpaceTrackTile } from "App/store/tracks/buildSpaceTrackTile";
import {
  childItemRowsToBulkSelections,
  useFormatFileToBaseEntityRow,
} from "../ExpandableSpace/ExpandableSpaceRow";
import { ENTITY_TYPE, EntityRowConfig } from "../config";
import { useFormatTrackVersionToBaseEntityRow } from "./helpers";

import styles from "./ExpandableTrackRow.module.scss";
import { ViewingSpaceId } from "App/modules/viewingSpace";
// TODO: Potential circular dependency between ExpandableTrackRow and ExpandableSpaceRow
// through childItemRowsToBulkSelections. This should be moved to a shared utility file
// to prevent circular imports and improve code organization.
// NOTE: Do not move this hook to helpers.ts as it would create another circular dependency and
// the other helper functions will cause an error wherever they are used.
export const useTrackChildItemRows = (
  track: Track,
  // The space the track's row is listed under — see `useTrackFiles`.
  viewingSpaceId?: ViewingSpaceId,
) => {
  // The caller's value (the list this row sits in) wins; a row-backed tile
  // carries the same space itself. Never `useUrlContext()`: this hook also
  // runs inside SpacePicker, which is mounted persistently in the nav
  // sidebar and outlives any route. A plain `Track` (Search, Library, ...)
  // has neither and falls back to the origin, which is the old behaviour.
  const resolvedViewingSpaceId =
    viewingSpaceId ?? (track as SpaceTrackTile).viewingSpaceId;
  const {
    versions,
    attachments,
    loading: trackVersionsLoading,
  } = useTrackFiles(track, resolvedViewingSpaceId);
  const defaultVersionId = getDefaultVersionId(track);
  // Comments are per (spaceId, trackId), so a borrowed track's thread lives
  // under the space it is being VIEWED from, not under `track.spaceId` (its
  // origin).
  const { fetchedComments, commentsLoading } = useTrackComments(
    track,
    resolvedViewingSpaceId,
  );

  const { formatTrackVersionToBaseEntityRow } =
    useFormatTrackVersionToBaseEntityRow();
  const { formatFileToBaseEntityRow } = useFormatFileToBaseEntityRow();

  const sortTrackVersions = useCallback(
    (a: FileEntity, b: FileEntity) => {
      const versionFilesV2 = track.versionFilesV2 || [];
      const versionFilesV2Length = versionFilesV2.length || 0;

      // Sort based on the index of version.id in versionFilesV2
      const indexA = versionFilesV2.indexOf(a.id);
      const indexB = versionFilesV2.indexOf(b.id);

      // If a version id is not found in versionFilesV2, it will return -1, placing it at the end.
      const adjustedIndexA = indexA === -1 ? versionFilesV2Length : indexA;
      const adjustedIndexB = indexB === -1 ? versionFilesV2Length : indexB;

      return adjustedIndexA - adjustedIndexB;
    },
    [track.versionFilesV2],
  );

  const sortedTrackVersions = useMemo(
    () =>
      versions.sort(sortTrackVersions).map((version, index) => {
        return formatTrackVersionToBaseEntityRow({
          version,
          defaultVersionId,
          track,
          commentCount: fetchedComments[version.id]?.length,
          options: {
            hasPlayableTracks: true,
            tableRowVariant: "expanded-row",
          },
          index,
        });
      }) || [],
    [versions, fetchedComments],
  );

  const allTrackChildEntityRows = useMemo(() => {
    const childTrackAttachments = attachments.map((attachment) => {
      return formatFileToBaseEntityRow(attachment);
    });

    // Hide versions if there's only one — a single version is redundant with the parent track row
    const showVersions = sortedTrackVersions.length > 1;
    return [
      ...(showVersions ? sortedTrackVersions : []),
      ...childTrackAttachments,
    ];
  }, [sortedTrackVersions, attachments]);

  return {
    allTrackChildEntityRows,
    allTrackChildRowsLoading: trackVersionsLoading || commentsLoading,
    sortedTrackVersions,
    trackVersionsLoading,
  };
};

const _ExpandableParentTrackRow = ({
  track,
  parentRow,
  tableRowProps,
}: {
  track: Track;
  parentRow: EntityRowConfig;
  tableRowProps: Omit<BaseTableRowProps, "row"> & {
    hasPlayableTracks?: boolean;
  };
  sortType?: SORT_TYPE;
}) => {
  const { addToSelection, bulkAddToSelection, selectedEntities } =
    useEntitiesSelection();
  const { preloadTrackVersions } = useGlobalAudioPlayer();
  const { allTrackChildEntityRows, allTrackChildRowsLoading } =
    useTrackChildItemRows(track, parentRow.viewingSpaceId);
  const { isMobile } = useViewport();
  const { registerDnDContainer, unregisterDnDContainer } = useDnDMonitor();

  useEffect(() => {
    // Preload here instead of "handleOnTogglePlay" to avoid unnecessary preload
    // on each track click. They all get preloaded when the track is expanded.
    if ((track?.versionFilesV2 || []).length > 0) {
      preloadTrackVersions(track.id, track.versionFilesV2);
    }
  }, [allTrackChildEntityRows]);

  const isTrackSelected = parentRow.id in selectedEntities;
  const allChildrenSelected = useMemo(() => {
    return (
      allTrackChildEntityRows.length > 0 &&
      allTrackChildEntityRows.every((itemRow) => itemRow.id in selectedEntities)
    );
  }, [allTrackChildEntityRows, selectedEntities]);

  useEffect(() => {
    if (isTrackSelected) {
      const bulkSelections = childItemRowsToBulkSelections(
        allTrackChildEntityRows,
        track.id,
        COLLECTION_ID.TRACK,
      );
      bulkAddToSelection(bulkSelections);
      return;
    }
    if (allChildrenSelected) {
      addToSelection({
        entityId: track.id,
        entityType: COLLECTION_ID.TRACK,
        ...(track.spaceId && {
          parentEntity: {
            entityId: track.spaceId,
            entityType: COLLECTION_ID.SPACE,
          },
        }),
        // The space this row is listed in — NOT track.spaceId above (that's
        // the track's ORIGIN, used only for the unrelated
        // parentEntity/sanitizeSelections bookkeeping), and NOT the route's
        // space (a nested child-space row sits on the PARENT's page).
        // Sending the origin here would make the bulk hard-delete guard's
        // comparison always equal, silently defeating it for a borrowed
        // track auto-selected via this "all children selected" cascade;
        // sending the route's space would trip it on a legitimate delete.
        ...(parentRow.viewingSpaceId && {
          spaceId: parentRow.viewingSpaceId,
        }),
      });
    }
  }, [
    isTrackSelected,
    allChildrenSelected,
    allTrackChildEntityRows,
    parentRow.viewingSpaceId,
  ]);

  // Register this track's child list as a container in the DnD registry
  useEffect(() => {
    registerDnDContainer(track.id, {
      getItemsOrder: () => allTrackChildEntityRows.map((r) => r.id),
      // NOTE: We do not apply reorder for `combine-and-reorder` due to the race condition mentioned in the comment DnDMonitor.tsx
      // It is guarded by a finalDestApi.containerEntityType check in the combine-and-reorder case.
      applyReorder: (newOrder) => {
        // Separate version ids and attachments
        // Currently, versions and attachments are sorted separately via `versionFilesV2` and `files` respectively.
        // This is why we need to separate them here.
        const { versionIds, attachments } = newOrder.reduce(
          (acc, id) => {
            const row = allTrackChildEntityRows.find((r) => r.id === id);
            if (row?.type === ENTITY_TYPE.TRACK_VERSION) {
              acc.versionIds.push(id);
            } else {
              acc.attachments.push(id);
            }
            return acc;
          },
          { versionIds: [], attachments: [] },
        );

        if (versionIds.length > 0 || attachments.length > 0) {
          // Check if the order actually changed before making server calls
          const currentVersionOrder = track.versionFilesV2 || [];
          const currentFileOrder = track.files || [];

          const versionOrderChanged =
            versionIds.length > 0 &&
            JSON.stringify(versionIds) !== JSON.stringify(currentVersionOrder);

          const fileOrderChanged =
            attachments.length > 0 &&
            JSON.stringify(attachments) !== JSON.stringify(currentFileOrder);

          // Only update if something actually changed
          if (versionOrderChanged || fileOrderChanged) {
            highnote.updateTrack({
              id: track.id,
              data: {
                ...(versionOrderChanged && { versionFilesV2: versionIds }),
                ...(fileOrderChanged && { files: attachments }),
              },
            });
          }
        }
      },
      containerEntityType: ENTITY_TYPE.TRACK,
    });
    return () => unregisterDnDContainer(track.id);
  }, [
    track.id,
    allTrackChildEntityRows,
    registerDnDContainer,
    unregisterDnDContainer,
  ]);

  if (allTrackChildRowsLoading && allTrackChildEntityRows.length === 0) {
    const childIndentation = calculateChildIndentation(
      tableRowProps.indentation,
      isMobile,
    );

    const defaultColumnWidth = isMobile
      ? EXPAND_COLUMN_WIDTH_MOBILE
      : EXPAND_COLUMN_WIDTH;

    return (
      <div
        className={styles["skeleton-container"]}
        style={{
          paddingLeft: `${childIndentation + defaultColumnWidth}px`,
        }}
      >
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={`skeleton-${index}`} height={40} />
        ))}
      </div>
    );
  }

  return (
    <>
      {allTrackChildEntityRows.map((childRow, i) => {
        const baseProps = {
          ...tableRowProps,
          parentRow,
          indentation: tableRowProps.indentation + 1,
        };

        return (
          <DnDWrapper
            key={`expandable-track-row-${childRow.key}-${i}`}
            isDraggable
            isDropTarget
            dragData={{
              id: childRow.id,
              name: childRow.name,
              type: childRow.type,
              parentId: track.id,
              containerId: track.id,
            }}
            entityData={childRow}
            selectedEntities={selectedEntities}
            dropData={{
              id: childRow.id,
              containerId: track.id,
              type: childRow.type,
            }}
          >
            <BaseRow
              {...baseProps}
              enableDrag={true}
              row={{
                ...childRow,
                selected: childRow.id in selectedEntities,
              }}
            />
          </DnDWrapper>
        );
      })}
    </>
  );
};

export const ExpandableParentTrackRow = ({
  parentRow,
  tableRowProps,
  sortType,
}: {
  parentRow: EntityRowConfig;
  tableRowProps: Omit<BaseTableRowProps, "row">;
  sortType?: SORT_TYPE;
}) => {
  const parentTrack = parentRow.entity as Track;

  return (
    <_ExpandableParentTrackRow
      track={parentTrack}
      parentRow={parentRow}
      tableRowProps={tableRowProps}
      sortType={sortType}
    />
  );
};
